<script setup>
import AppLogo from "./AppLogo.vue";
import { defineProps, ref, onMounted, watch } from "vue";
import folders from "../../services/folders";
import { useAppStore } from "../../../../store/app";
import { validMinNumber } from "@/utils/helper";
import docs from "../../services/docs";
import { useDocsStore } from "../../store/docs";
import { useUserStore } from "../../../../store/user";

const props = defineProps(['isDrawerOpen'])
const items = ref([])
const app = useAppStore()
const docsStore = useDocsStore()
const isAdmin = ref(useUserStore().isInRole('admin'))

onMounted(async () => {
  const result = await folders.getFolders()
  items.value = result
  setKey('', items.value)
})

function getKey(i, p) {
  if (p) return p.key + '_' + i.type + '_' + i.id
  return i.type + '_' + i.id
}

function setKey(p, a) {
  for (let i = 0; i < a.length; i++) {
    a[i].key = getKey(a[i], p)
    if (a[i].children) setKey(a[i], a[i].children)
  }
}

function addDoc(parent) {
  app.showInputBox('New Document', '', async (v) => {
    const result = await docs.create({
      name: v,
      status: 'visible',
      orderNo: items.value.length + 1,
      parentId: parent ? parent.id : 0
    })

    result.type = 'doc'
    result.key = getKey(result, parent)

    if (parent) {
      if (!parent.children) parent.children = []
      parent.children.push(result)
    } else
      items.value.push(result)
  })
}

function addFolder(parent) {
  app.showInputBox('New Folder', '', async (v) => {
    const result = await folders.create({
      name: v,
      status: 'visible',
      orderNo: items.value.length + 1,
      parentId: parent ? parent.id : 0
    })

    result.type = 'folder'
    result.key = getKey(result, parent)

    if (parent) {
      if (!parent.children) parent.children = []
      parent.children.push(result)
    } else
      items.value.push(result)
  })
}

function getParentByKey(p, k) {
  for (let i = 0; i < p.length; i++) {
    if (p[i].key == k) return p
    if (p[i].children) {
      const o = getParentByKey(p[i].children, k)
      if (o) return o
    }
  }

  return null
}

function deleteItem(item) {
  app.showMessageBox('confirm', 'Do you want to delete selected item?', async () => {
    if (item.type == 'doc') {
      const result = await docs.delete(item.id)
      if (!result.success) {
        app.showMessageBox('error', result.errorMessage)
        return
      }
    } else {
      const result = await folders.delete(item.id)
      if (!result.success) {
        app.showMessageBox('error', result.errorMessage)
        return
      }
    }

    const parent = getParentByKey(items.value, item.key)
    if (parent) parent.splice(parent.indexOf(item), 1)
    else items.value.splice(items.value.indexOf(item), 1)
  })
}

function itemClick(e) {
  docsStore.setActiveItem(e)
}

function rename(item) {
  app.showInputBox('Rename', item.name, async (v) => {
    const d = {}
    Object.assign(d, item)
    d.name = v
    let result

    if (item.type == 'folder') {
      result = await folders.update(item.id, d)
    } else {
      result = await docs.update(item.id, d)
    }

    if (!result.success) {
      app.showMessageBox('error', result.errorMessage)
      return
    }

    item.name = v
    app.showMessage('Update completed.')
  })
}
</script>

<template>
  <v-navigation-drawer :value="isDrawerOpen" app :floating="!app.config.sideBarShowBorder" width="330px"
    class="app-navigation-menu" :right="$vuetify.rtl" @input="(val) => $emit('update:is-drawer-open', val)">
    <AppLogo />

    <v-toolbar v-if="isAdmin" dense flat>
      <v-btn small outlined text @click="addFolder(null)">
        <v-icon left>mdi-folder-plus-outline</v-icon>
        Folder
      </v-btn>
      <v-btn class="ml-2" small outlined text @click="addDoc(null)">
        <v-icon left>mdi-file-document-plus-outline</v-icon>
        Document
      </v-btn>
    </v-toolbar>
    <v-divider></v-divider>

    <v-treeview item-key="key" :items="items" open-on-click :activatable="true">
      <template v-slot:prepend="{ item }">
        <v-icon v-if="item.type == 'folder'">
          {{ item.icon ? item.icon : 'mdi-folder-outline' }}
        </v-icon>
        <v-icon v-else>
          {{ item.icon ? item.icon : 'mdi-file-outline' }}
        </v-icon>
      </template>
      <template v-slot:label="{ item }">
        <div @click="itemClick(item)">{{ item.name }}</div>
      </template>
      <template v-slot:append="{ item }">
        <v-menu v-if="isAdmin">
          <template v-slot:activator="{ on, }">
            <v-btn icon v-on="on">
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="addFolder(item)">
              <v-list-item-action>
                <v-icon>mdi-folder-plus-outline</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                Sub Folder
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="addDoc(item)">
              <v-list-item-action>
                <v-icon>mdi-file-document-plus-outline</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                Document
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item @click="rename(item)">
              <v-list-item-action>
                <v-icon>mdi-square-edit-outline</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                Rename
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item @click="deleteItem(item)">
              <v-list-item-action>
                <v-icon>mdi-delete-outline</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                Delete
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-treeview>
  </v-navigation-drawer>
</template>
